import React, { lazy, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Navbar from "./Navbar";
import Footer from "./Footer";

import Swal from "sweetalert2";
import { Helmet } from "react-helmet-async";

const PhoneInput = lazy(() => import("react-phone-input-2"));

/* ============================================================
   EMPTY FORM
============================================================ */

const emptyForm = {
  name: "",
  company: "",
  email: "",
  countryCode: "+91",
  phone: "",
  service: "",
  timeline: "",
  requirements: "",
};

/* ============================================================
   REGEX
============================================================ */

const nameRegex = /^[A-Za-z]+(?: [A-Za-z]+)*$/;

const companyRegex = /^[A-Za-z0-9][A-Za-z0-9 .,&'()\-]*$/;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* ============================================================
   SAFE JSON
============================================================ */

async function safeJson(res) {
  const text = await res.text();

  try {
    return JSON.parse(text);
  } catch (error) {
    console.error("Non-JSON response from server:", text.slice(0, 500));

    throw new Error(`Server did not return valid JSON (status ${res.status}).`);
  }
}

/* ============================================================
   CONSULTATION PAGE
============================================================ */

function Consultation() {
  const navigate = useNavigate();

  /* ============================================================
     STATE
  ============================================================ */

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  /* ============================================================
     HELPERS
  ============================================================ */

  const cleanName = (value) => {
    return value
      .replace(/[^A-Za-z ]/g, "")
      .replace(/\s{2,}/g, " ")
      .replace(/^\s+/, "");
  };

  const cleanCompany = (value) => {
    return value
      .replace(/[^A-Za-z0-9 .,&'()\-]/g, "")
      .replace(/\s{2,}/g, " ")
      .replace(/^\s+/, "");
  };

  const cleanText = (value) => {
    return value
      .replace(/[<>]/g, "")
      .replace(/\s{2,}/g, " ")
      .replace(/^\s+/, "");
  };

  /* ============================================================
     FORM CHANGE
  ============================================================ */

  const handleChange = (e) => {
    const { name, value } = e.target;

    let nextValue = value;

    if (name === "name") {
      nextValue = cleanName(value).slice(0, 50);
    }

    if (name === "company") {
      nextValue = cleanCompany(value).slice(0, 100);
    }

    if (name === "email") {
      nextValue = value.replace(/\s/g, "").toLowerCase();
    }

    if (name === "requirements") {
      nextValue = cleanText(value);
    }

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    setFormError("");
  };

  /* ============================================================
     VALIDATION
  ============================================================ */

  const validateForm = () => {
    const name = form.name.trim();
    const company = form.company.trim();
    const email = form.email.trim();
    const requirements = form.requirements.trim();
    const phone = form.phone.replace(/\D/g, "");

    if (!name) {
      return "Full Name is required.";
    }

    if (name.length < 3) {
      return "Full Name must contain at least 3 characters.";
    }

    if (!nameRegex.test(name)) {
      return "Name can contain only alphabets and spaces.";
    }

    if (!company) {
      return "Company / Organization is required.";
    }

    if (company.length < 3) {
      return "Company / Organization must contain at least 3 characters.";
    }

    if (company.length > 100) {
      return "Company / Organization cannot exceed 100 characters.";
    }

    if (!companyRegex.test(company)) {
      return "Company name contains invalid characters.";
    }

    if (!email) {
      return "Email Address is required.";
    }

    if (!emailRegex.test(email)) {
      return "Please enter a valid email address.";
    }

    if (!phone) {
      return "Phone number is required.";
    }

    if (phone.length < 6) {
      return "Please enter a valid phone number.";
    }

    if (!form.service) {
      return "Please select a service.";
    }

    if (!form.timeline) {
      return "Please select a project timeline.";
    }

    if (requirements.length < 10) {
      return "Requirement details must be at least 10 characters.";
    }

    return "";
  };

  /* ============================================================
     SUBMIT
  ============================================================ */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormError("");

    const validationMessage = validateForm();

    if (validationMessage) {
      setFormError(validationMessage);

      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: validationMessage,
        confirmButtonColor: "#2563eb",
      });

      return;
    }

    setSubmitting(true);

    try {
      const fd = new FormData();

      fd.append("name", form.name.trim());

      fd.append("company", form.company.trim());

      fd.append("email", form.email.trim());

      fd.append("phone", `${form.countryCode}${form.phone}`);

      fd.append("service", form.service);

      fd.append("timeline", form.timeline);

      fd.append("requirements", form.requirements.trim());

      /* ========================================================
         API
      ======================================================== */

      const res = await fetch(
        "https://swiftintelli.com/api/save_consultation.php",
        {
          method: "POST",
          body: fd,
        },
      );

      const data = await safeJson(res);

      /* ========================================================
         SUCCESS
      ======================================================== */

      if (data?.success) {
        await Swal.fire({
          icon: "success",
          title: "Request Sent!",
          text: "Your consultation request has been submitted successfully. Our team will get back to you shortly.",
          confirmButtonColor: "#2563eb",
        });

        setForm(emptyForm);
        setFormError("");

        navigate("/");

        return;
      }

      /* ========================================================
         DUPLICATE
      ======================================================== */

      if (data?.duplicate) {
        Swal.fire({
          icon: "info",
          title: "Already Submitted",
          text:
            data.message ||
            "You've already requested a consultation for this service. Our team will reach out to you shortly.",
          confirmButtonColor: "#2563eb",
        });

        return;
      }

      /* ========================================================
         API ERROR
      ======================================================== */

      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: data?.message || "Something went wrong. Please try again.",
        confirmButtonColor: "#2563eb",
      });
    } catch (error) {
      console.error("Consultation submission error:", error);

      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        text:
          error.message ||
          "Unable to reach the server. Please check your connection and try again.",
        confirmButtonColor: "#2563eb",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     JSX
  ============================================================ */

  return (
    <>
    <Helmet>

  <title>
    Free Tech Consultation | Get Expert Guidance Fast
  </title>

  <meta
    name="description"
    content="Share your business or career goals with SWIFTIntelli. Get a free consultation to map out the ideal custom software, integration, or AI technology approach."
  />

</Helmet>
      {/* ========================================================
          NAVBAR
      ======================================================== */}

      <Navbar />

      {/* ========================================================
          PAGE HEADER
      ======================================================== */}

      <header className="blog-masthead">
        <div className="blog-masthead-overlay"></div>

        <div className="blog-masthead-content">
          <div className="blog-breadcrumb">
            <Link to="/">Home</Link>

            <span> / </span>

            <span>Consultations</span>
          </div>

          <h1>Consultations</h1>
        </div>
      </header>

      {/* ========================================================
          CONSULTATION PAGE
      ======================================================== */}

      <main className="demo-page">
        <div className="demo-layout">
          {/* ====================================================
              LEFT INFORMATION
          ==================================================== */}

          <aside className="demo-info">
            <span className="demo-label">FREE CONSULTATION</span>

            <h2>
              Let's build something
              <span> meaningful </span>
              together.
            </h2>

            <p>
              Speak with our team about your technology, software development,
              AI, consulting, or corporate training requirements.
            </p>

            {/* ==================================================
                BENEFITS
            ================================================== */}

            <div className="demo-benefits">
              {/* BENEFIT 01 */}

              <div className="demo-benefit">
                <span>01</span>

                <div>
                  <h3>Understand Your Needs</h3>

                  <p>
                    Tell us about your goals, challenges, and business
                    requirements.
                  </p>
                </div>
              </div>

              {/* BENEFIT 02 */}

              <div className="demo-benefit">
                <span>02</span>

                <div>
                  <h3>Explore the Right Solution</h3>

                  <p>
                    Our team will help identify the right technology,
                    development, or training approach.
                  </p>
                </div>
              </div>

              {/* BENEFIT 03 */}

              <div className="demo-benefit">
                <span>03</span>

                <div>
                  <h3>Get Expert Guidance</h3>

                  <p>
                    Discuss your requirements with our team and understand the
                    next steps.
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* ====================================================
              CONSULTATION FORM
          ==================================================== */}

          <section className="demo-form-card">
            <form className="demo-form" onSubmit={handleSubmit}>
              {/* ==================================================
                  NAME + COMPANY
              ================================================== */}

              <div className="demo-form-grid">
                {/* NAME */}

                <div className="demo-field">
                  <label>
                    Full Name <b>*</b>
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    minLength={3}
                    maxLength={50}
                    autoComplete="name"
                    required
                  />
                </div>

                {/* COMPANY */}

                <div className="demo-field">
                  <label>
                    Company / Organization <b>*</b>
                  </label>

                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Enter your company name"
                    minLength={3}
                    maxLength={100}
                    autoComplete="organization"
                    required
                  />
                </div>
              </div>

              {/* ==================================================
                  EMAIL + PHONE
              ================================================== */}

              <div className="demo-form-grid">
                {/* EMAIL */}

                <div className="demo-field">
                  <label>
                    Email Address <b>*</b>
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                </div>

                {/* PHONE */}

                <div className="demo-field">
                  <label>
                    Phone Number <b>*</b>
                  </label>

                  <PhoneInput
                    country="in"
                    value={`${form.countryCode.replace("+", "")}${form.phone}`}
                    onChange={(value, country) => {
                      const dialCode = country?.dialCode || "91";

                      const number = value
                        .replace(new RegExp(`^${dialCode}`), "")
                        .replace(/\D/g, "");

                      setForm((prev) => ({
                        ...prev,
                        countryCode: `+${dialCode}`,
                        phone: number,
                      }));

                      setFormError("");
                    }}
                    enableSearch
                    searchPlaceholder="Search country"
                    searchNotFound="Country not found"
                    countryCodeEditable={false}
                    preferredCountries={["in", "us", "gb", "ae", "sg", "au"]}
                    inputProps={{
                      name: "phone",
                      required: true,
                      autoComplete: "tel",
                    }}
                    inputClass="consultation-phone-input"
                    containerClass="consultation-phone-container"
                    buttonClass="consultation-phone-button"
                    dropdownClass="consultation-phone-dropdown"
                  />
                </div>
              </div>

              {/* ==================================================
                  SERVICE + TIMELINE
              ================================================== */}

              <div className="demo-form-grid">
                {/* SERVICE */}

                <div className="demo-field">
                  <label>
                    Service Required <b>*</b>
                  </label>

                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select a service
                    </option>

                    <option value="AI Solutions">AI Solutions</option>

                    <option value="Software Development">
                      Software Development
                    </option>

                    <option value="Technology Consulting">
                      Technology Consulting
                    </option>

                    <option value="Corporate Training">
                      Corporate Training
                    </option>

                    <option value="Proposal Request">Proposal Request</option>
                  </select>
                </div>

                {/* TIMELINE */}

                <div className="demo-field">
                  <label>
                    Project Timeline <b>*</b>
                  </label>

                  <select
                    name="timeline"
                    value={form.timeline}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select timeline
                    </option>

                    <option value="Immediate">Immediate</option>

                    <option value="Within 1 Month">Within 1 Month</option>

                    <option value="1-3 Months">1–3 Months</option>

                    <option value="Exploring Options">Exploring Options</option>
                  </select>
                </div>
              </div>

              {/* ==================================================
                  REQUIREMENTS
              ================================================== */}

              <div className="demo-field">
                <label>
                  Requirement Details <b>*</b>
                </label>

                <textarea
                  name="requirements"
                  value={form.requirements}
                  onChange={handleChange}
                  placeholder="Describe your project, software, AI, consulting, or training requirements..."
                  minLength={10}
                  maxLength={1000}
                  rows={6}
                  required
                />
              </div>

              {/* ==================================================
                  FORM ERROR
              ================================================== */}

              {formError && (
                <div className="consultation-form-error" role="alert">
                  {formError}
                </div>
              )}

              {/* ==================================================
                  SUBMIT
              ================================================== */}

              <button
                type="submit"
                className="demo-submit"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Schedule Consultation"}
              </button>
            </form>
          </section>
        </div>
      </main>

      {/* ========================================================
          FOOTER
      ======================================================== */}

      <Footer />
    </>
  );
}

export default Consultation;
